import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Paper,
  Chip,
  Button,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonIcon from "@mui/icons-material/Person";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backButton: {
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #ddd",
    padding: "8px 16px",
    minWidth: "auto",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  sectionCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  sectionTitle: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  fieldRow: {
    marginBottom: theme.spacing(2),
  },
  fieldRowDouble: {
    display: "flex",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(3),
    "& > div": {
      flex: 1,
    },
  },
  fieldLabel: {
    color: "#666",
    fontSize: "0.85rem",
    marginBottom: theme.spacing(0.5),
    fontWeight: "400",
  },
  fieldValue: {
    fontWeight: "600",
    fontSize: "0.95rem",
    color: "#333",
  },
  statusChip: {
    borderRadius: "20px",
    fontWeight: "bold",
    fontSize: "0.75rem",
  },
  imagePreview: {
    maxWidth: "150px",
    maxHeight: "150px",
    borderRadius: "8px",
    cursor: "pointer",
  },

}));

const TripDetails = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const tripData = location.state?.tripData;

  const handleBack = () => {
    navigate(-1);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
      case 'COMPLETADO':
        return { backgroundColor: '#d4edda', color: '#155724' };
      case 'NEW':
      case 'NUEVO':
        return { backgroundColor: '#d1ecf1', color: '#0c5460' };
      case 'ACCEPTED':
      case 'ACEPTADO':
        return { backgroundColor: '#cce5ff', color: '#004085' };
      case 'PENDING':
      case 'PENDIENTE':
        return { backgroundColor: '#fff3cd', color: '#856404' };
      case 'CANCELLED':
      case 'CANCELADO':
        return { backgroundColor: '#f8d7da', color: '#721c24' };
      default:
        return { backgroundColor: '#e2e3e5', color: '#383d41' };
    }
  };

  if (!tripData) {
    return (
      <div className={classes.container}>
        <Typography variant="h6">{t('no_trip_data_available')}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div>
            <Typography variant="h4" style={{ fontWeight: "bold" }}>
              {t('trip_details')}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {t('trip_reference')}: {tripData.reference || t('na')}
            </Typography>
          </div>
        </div>
        <Button 
          className={classes.backButton} 
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          {t('back')}
        </Button>
      </div>

      <Grid container spacing={3}>
        {/* First Block - Detalles de Reserva */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              <LocationOnIcon color="primary" />
              {t('booking_details')}
            </Typography>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('booking_reference_label')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.reference || t('dash')}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('booking_date')}</Typography>
                <Typography className={classes.fieldValue}>
                  {tripData.bookingDate ? new Date(tripData.bookingDate).toLocaleDateString() + " " + new Date(tripData.bookingDate).toLocaleTimeString() : t('dash')}
                </Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('vehicle_type')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.carType || t('dash')}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('assigned_driver')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.driver_name || t('not_assigned')}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('booking_status')}</Typography>
                <div>
                  <Chip
                    label={tripData.status || t('na')}
                    className={classes.statusChip}
                    style={getStatusColor(tripData.status)}
                  />
                </div>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('trip_pin')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.otp || t('dash')}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('trip_cost')}</Typography>
              <Typography className={classes.fieldValue} style={{ fontWeight: "bold", color: "#2e7d32" }}>
                ${tripData.trip_cost || '0.00'}
              </Typography>
            </div>
          </Paper>
        </Grid>

        {/* Second Block - Detalles del Viaje */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              <AccessTimeIcon color="primary" />
              {t('trip_details_section')}
            </Typography>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('booking_id')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.id || t('dash')}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('booking_reference_label')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.reference || t('dash')}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('booking_status')}</Typography>
                <div>
                  <Chip
                    label={tripData.status || t('na')}
                    className={classes.statusChip}
                    style={getStatusColor(tripData.status)}
                  />
                </div>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('distance')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.distance || tripData.estimateDistance || t('dash')} km</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('trip_start_time')}</Typography>
                <Typography className={classes.fieldValue}>
                  {tripData.trip_start_time ? new Date(tripData.trip_start_time).toLocaleString() : t('dash')}
                </Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('trip_end_time')}</Typography>
                <Typography className={classes.fieldValue}>
                  {tripData.trip_end_time ? new Date(tripData.trip_end_time).toLocaleString() : t('dash')}
                </Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('total_time')}</Typography>
                <Typography className={classes.fieldValue}>
                  {tripData.estimateTime || '35 minutos'}
                </Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('driver_rating')}</Typography>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ color: '#ffd700', fontSize: '16px' }}>⭐</span>
                  <Typography className={classes.fieldValue}>
                    {tripData.driverRating || '0'}/5
                  </Typography>
                </div>
              </div>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('trip_instructions')}</Typography>
              <Typography className={classes.fieldValue}>
                {tripData.tripInstructions || t('dash')}
              </Typography>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('cancellation_reason')}</Typography>
              <Typography className={classes.fieldValue} style={{ color: "#d32f2f" }}>
                {tripData.reason || t('dash')}
              </Typography>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('pickup_address')}</Typography>
              <Typography className={classes.fieldValue}>
                {tripData.pickupAddress || t('dash')}
              </Typography>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('dropoff_address')}</Typography>
              <Typography className={classes.fieldValue}>
                {tripData.dropAddress || t('dash')}
              </Typography>
            </div>
          </Paper>
        </Grid>

        {/* Third Block - Información de Pago */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              <PaymentIcon color="primary" />
              {t('payment_information')}
            </Typography>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('trip_cost')}</Typography>
                <Typography className={classes.fieldValue}>${tripData.trip_cost || '0.00'}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('discount_amount')}</Typography>
                <Typography className={classes.fieldValue}>${tripData.discount || '0.00'}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('driver_share')}</Typography>
                <Typography className={classes.fieldValue}>${tripData.driver_share || '0.00'}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('fleet_admin_fee')}</Typography>
                <Typography className={classes.fieldValue}>${tripData.fleetCommission || tripData.convenience_fees || '0.00'}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('cancellation_fee')}</Typography>
                <Typography className={classes.fieldValue}>${tripData.cancellationFee || '0.00'}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('customer_paid')}</Typography>
                <Typography className={classes.fieldValue}>${tripData.customer_paid || tripData.trip_cost || '0.00'}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('payment_mode')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.payment_mode || t('dash')}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('payment_gateway')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.gateway || t('dash')}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('cash_payment_amount')}</Typography>
                <Typography className={classes.fieldValue}>${tripData.cashPaymentAmount || '0.00'}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('card_payment_amount')}</Typography>
                <Typography className={classes.fieldValue}>${tripData.cardPaymentAmount || '0.00'}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('wallet_payment_amount')}</Typography>
              <Typography className={classes.fieldValue}>${tripData.usedWalletMoney || '0.00'}</Typography>
            </div>
          </Paper>
        </Grid>

        {/* Fourth Block - Información del Pasajero */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              <PersonIcon color="primary" />
              {t('passenger_information')}
            </Typography>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('client_id')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.customer || t('dash')}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('customer_name')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.customer_name || t('dash')}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('customer_phone')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.customer_contact || t('dash')}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('customer_email')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.customer_email || t('dash')}</Typography>
              </div>
            </div>
          </Paper>
        </Grid>

        {/* Fifth Block - Información del Conductor */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              <DriveEtaIcon color="primary" />
              {t('driver_information')}
            </Typography>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('assigned_driver')}</Typography>
              <Typography className={classes.fieldValue}>{tripData.driver_name || t('not_assigned')}</Typography>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('vin_number')}</Typography>
              <Typography className={classes.fieldValue}>{tripData.vehicle_number || tripData.vehicleNumber || t('dash')}</Typography>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('vehicle_type')}</Typography>
              <Typography className={classes.fieldValue}>{tripData.carType || t('dash')}</Typography>
            </div>
          </Paper>
        </Grid>

        {/* Sixth Block - Información del Paquete */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.sectionCard}>
            <Typography className={classes.sectionTitle}>
              <LocalShippingIcon color="primary" />
              {t('package_information')}
            </Typography>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('receiving_person_name')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.deliveryPerson || t('dash')}</Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('receiving_person_phone')}</Typography>
                <Typography className={classes.fieldValue}>{tripData.deliveryPersonPhone || t('dash')}</Typography>
              </div>
            </div>
            
            <div className={classes.fieldRowDouble}>
              <div>
                <Typography className={classes.fieldLabel}>{t('parcel_type')}</Typography>
                <Typography className={classes.fieldValue}>
                  {tripData.parcelTypeSelected?.description || t('dash')}
                </Typography>
              </div>
              <div>
                <Typography className={classes.fieldLabel}>{t('parcel_option')}</Typography>
                <Typography className={classes.fieldValue}>
                  {tripData.optionSelected?.description || t('dash')}
                </Typography>
              </div>
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('pickup_image')}</Typography>
              {tripData.pickup_image ? (
                <img 
                  src={tripData.pickup_image} 
                  alt="Pickup" 
                  className={classes.imagePreview}
                  onClick={() => window.open(tripData.pickup_image, '_blank')}
                />
              ) : (
                <Typography className={classes.fieldValue}>{t('dash')}</Typography>
              )}
            </div>
            
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>{t('delivery_image')}</Typography>
              {tripData.deliver_image ? (
                <img 
                  src={tripData.deliver_image} 
                  alt="Delivery" 
                  className={classes.imagePreview}
                  onClick={() => window.open(tripData.deliver_image, '_blank')}
                />
              ) : (
                <Typography className={classes.fieldValue}>{t('dash')}</Typography>
              )}
            </div>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default TripDetails;